import React from 'react';
import DiagnosticCentral from './pages/DiagnosticCentral';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { Navbar } from './components/layout/Navbar.jsx';
import { Footer } from './components/layout/Footer';

import Home from './pages/Home';
import AILab from './pages/AILab';
import AcousticAnalysis from './pages/AcousticAnalysis';
import ImageAnalysis from './pages/ImageAnalysis';
import PathologyAnalysis from './pages/PathologyAnalysis';
import ClinicalAnalysis from './pages/ClinicalAnalysis';
import MolecularAnalysis from './pages/MolecularAnalysis';
import Platform from './pages/Platform';
import Technology from './pages/Technology';
import Insights from './pages/Insights';
import ResearchAgent from './pages/agents/ResearchAgent';
import Contact from './pages/Contact';
import XAIDemo from "./pages/XAIDemo";
import XAISandbox from "./components/XAISandbox";
import ClinicalHub from "./pages/ClinicalHub";

import Patients from "./pages/Patients";
import Capture from "./pages/Capture";
import Analytics from "./pages/Analytics";
import LiveDiagnosticCaseReview from "./pages/LiveDiagnosticCaseReview";

import MultimodalFusion from './pages/platform/MultimodalFusion';
import CTF from './pages/platform/CTF';

import RespiratoryScreening from './pages/solutions/RespiratoryScreening';
import ClinicalIntelligence from './pages/solutions/ClinicalIntelligence';
import PrecisionMedicine from './pages/solutions/PrecisionMedicine';
import Monitoring from './pages/solutions/Monitoring';

import About from './pages/company/About';
import Mission from './pages/company/Mission';
import Founder from './pages/company/Founder.tsx';
import Vision from './pages/company/Vision';
import Roadmap from './pages/company/Roadmap';
import Careers from './pages/company/Careers';
import News from './pages/company/News';
import ScientificAdvisoryBoard from './pages/company/ScientificAdvisoryBoard';
import Partnerships from './pages/company/Partnerships';

import InteractiveDemo from './pages/InteractiveDemo';
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import DemoRequest from './pages/DemoRequest';
import AnimatedDemoPage from './pages/AnimatedDemo';
import FAQ from './pages/FAQ';
import { DocumentViewer } from './pages/DocumentViewer.jsx';
import Login from './pages/Login';
import { AuthProvider } from './context/AuthContext';
import { useAnalytics } from './hooks/useAnalytics';

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <div className="min-h-screen flex flex-col">
          <Navbar />

          <main className="flex-grow">
            <Routes>
              <Route path="/diagnostic-central" element={<DiagnosticCentral />} />
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/interactive-demo" element={<InteractiveDemo />} />
              <Route path="/animated-demo" element={<AnimatedDemoPage />} />
              <Route path="/analytics-dashboard" element={<AnalyticsDashboard />} />
              <Route path="/demo-request" element={<DemoRequest />} />

              {/* Research & Validation */}
              <Route path="/insights" element={<Insights />} />

              {/* Redirect old /validation path to /insights */}
              <Route path="/validation" element={<Navigate to="/insights" replace />} />

              {/* FAQ */}
              <Route path="/faq" element={<FAQ />} />

              {/* Confidential documents (Resources menu): view-only, sign-in required */}
              <Route path="/resources/documents/:slug" element={<DocumentViewer />} />

              <Route path="/ai-lab" element={<AILab />} />
              <Route path="/acoustic-analysis" element={<AcousticAnalysis />} />
              <Route path="/image-analysis" element={<ImageAnalysis />} />
              <Route path="/pathology-analysis" element={<PathologyAnalysis />} />
              <Route path="/clinical-analysis" element={<ClinicalAnalysis />} />
              <Route path="/molecular-analysis" element={<MolecularAnalysis />} />
              <Route path="/platform/pathology-intelligence" element={<PathologyAnalysis />} />

              <Route path="/platform" element={<Platform />} />
              <Route path="/platform/acoustic-intelligence" element={<AcousticAnalysis />} />
              <Route path="/platform/radiology-intelligence" element={<ImageAnalysis />} />
              <Route path="/platform/multimodal-fusion" element={<MultimodalFusion />} />
              <Route path="/platform/ctf" element={<CTF />} />

              <Route path="/technology" element={<Technology />} />

              <Route path="/patients" element={<Patients />} />
              <Route path="/capture" element={<Capture />} />
              <Route path="/analytics" element={<Analytics />} />

              <Route path="/live-diagnostic-case-review" element={<LiveDiagnosticCaseReview />} />
              <Route path="/clinical-hub" element={<ClinicalHub />} />
              <Route path="/diagnostic-view" element={<XAIDemo />} />
              <Route path="/xai-sandbox" element={<XAISandbox />} />
              <Route path="/diagnostic-view/:platformId" element={<XAIDemo />} />
              <Route path="/diagnostic-view/:platformId/:exampleIndex" element={<XAIDemo />} />

              {/* Solutions */}
              <Route
                path="/solutions/respiratory-screening"
                element={<RespiratoryScreening />}
              />
              <Route
                path="/solutions/clinical-intelligence"
                element={<ClinicalIntelligence />}
              />
              <Route
                path="/solutions/precision-medicine"
                element={<PrecisionMedicine />}
              />
              <Route
                path="/solutions/monitoring"
                element={<Monitoring />}
              />

              {/* Agents */}
              <Route
                path="/agents/research-agent"
                element={<ResearchAgent />}
              />

              {/* Old menu URL: Diagnostics now lives at respiratory-screening */}
              <Route
                path="/solutions/diagnostics"
                element={
                  <Navigate
                    to="/solutions/respiratory-screening"
                    replace
                  />
                }
              />

              {/* Company */}
              <Route path="/company/about" element={<About />} />
              <Route path="/company/mission" element={<Mission />} />
              <Route path="/company/leadership" element={<Founder />} />
              <Route
                path="/company/scientific-advisory-board"
                element={<ScientificAdvisoryBoard />}
              />
              <Route path="/company/partnerships" element={<Partnerships />} />
              <Route path="/company/careers" element={<Careers />} />
              <Route path="/company/news" element={<News />} />
              <Route path="/company/founder" element={<Founder />} />
              <Route path="/company/vision" element={<Vision />} />
              <Route path="/company/roadmap" element={<Roadmap />} />
              <Route path="/company/contact" element={<Contact />} />
            </Routes>
          </main>

          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
